/* Sundays in Bed — "Price your home" calculator core.

   One place that turns a handful of answers (built-up area, bedrooms, bathrooms to redo,
   kitchen layout, new or older home, finish level) into the figures the calculators show.
   Used by sections/sib-cost-index.liquid (the eight renovation-cost pages) and the `calc`
   block in sections/sib-guide.liquid (the area guides, first used on Puchong).

   Nothing is estimated here. Renovation and furniture come from sib-boq-engine.js — the V9
   Klang Valley cost model that the quote flow and the lead emails use — and the design fee
   from sib-estimator.js, so a figure on a page, in the quote and in the email always agree.

   Until 26 Sep 2026 the cost pages ran their own copy of the seven-line WORKS model that
   sib-estimator.js marks SUPERSEDED: it quoted roughly half of what this engine does.

   The engine files are loaded on demand (load()), so a page only pays for them when its
   calculator is close to the screen. */
(function (root) {
  'use strict';

  /* Customer-facing finish letter -> the engine's own rate-card column. Never feed a display
     label (Curated, Bespoke) to the engine: it string-compares these exact names. */
  var TIER = { e: 'Essential', p: 'Premium', s: 'Signature' };
  var PTYPE = { condo: 'Condominium', apartment: 'Apartment / walk-up', terrace: 'Terrace', 'semi-d': 'Semi-D', bungalow: 'Bungalow' };

  function r100(n) { return Math.round(n / 100) * 100; }
  function ready() { return !!(root.SIBEstimator && root.SIBBoqEngine); }

  var loading = null;
  function inject(src) {
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src; s.async = false; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }
  /* urls: { est, data, engine } from Liquid's asset_url. The data file must load before the
     engine, whose factory reads window.SIBBoqData the moment it runs. */
  function load(urls) {
    if (ready()) return Promise.resolve();
    if (loading) return loading;
    var seq = [];
    if (!root.SIBEstimator) seq.push(urls.est);
    if (!root.SIBBoqData) seq.push(urls.data);
    if (!root.SIBBoqEngine) seq.push(urls.engine);
    loading = seq.reduce(function (p, src) { return p.then(function () { return inject(src); }); }, Promise.resolve());
    loading['catch'](function () { loading = null; });
    return loading;
  }

  /* The rooms a home is priced on. Living and dining count as one open-plan space; a study is a
     room of its own (the engine's Home office); the foyer
     and the yard are always there, and the fee engine makes them free under its own rule.
     kitchen: 'open'   = part of the open plan (no extra room)
              'sep'    = a separate kitchen (one room)
              'wetdry' = a dry kitchen and a wet kitchen (two rooms) */
  function roomCounts(o) {
    var c = { bed: o.bed | 0, bath: o.bath | 0, off: o.study | 0, opn: 1, util: 1, foyer: 1, kit: 0, wetkit: 0 };
    if (o.kitchen === 'sep') c.kit = 1;
    else if (o.kitchen === 'wetdry') { c.kit = 1; c.wetkit = 1; }
    return c;
  }

  /* Scope of works. A home just handed over keeps its floors and, unless you are redoing
     them, its bathrooms; an older home is priced for the full job. */
  function works(o) {
    var older = o.condition === 'older', wet = older || (o.bath | 0) > 0;
    return {
      plumbing: wet, wetTiling: wet, flooring: older,
      paintingCeiling: true, electrical: true, kitchenCarpentry: true,
      builtInWardrobe: (o.bed | 0) > 0, ffe: true
    };
  }

  function fee(o) {
    var c = roomCounts(o), counts = {}, k;
    for (k in root.SIBEstimator.ROOMS) counts[k] = c[k] || 0;
    return root.SIBEstimator.estimate({ counts: counts, svc: 'd', tier: 'e', sqft: o.sqft, works: [] });
  }

  /* o: { sqft, bed, bath, study, kitchen, condition: 'new'|'older', type: 'condo'|'terrace'|... }
     Returns the design fee and, per finish level, the renovation-and-furniture range and the
     all-in range (fee + works), rounded to the nearest RM100 like the quote. */
  function price(o) {
    var f = fee(o), out = { fee: f.fee, charged: f.chargeN, free: f.freeN, std: f.stdN, opn: f.opnN, tiers: {} };
    ['e', 'p', 's'].forEach(function (t) {
      var b = root.SIBBoqEngine.estimateProject({
        existingSiteCondition: o.condition === 'older' ? 'resale_older' : 'new_developer',
        propertyAgeYears: 0, propertyPhysicalCondition: 'Average',
        propertyType: PTYPE[o.type] || 'Condominium',
        finishTier: TIER[t], roomCounts: roomCounts(o), works: works(o),
        layoutChange: 'Keep layout', projectPurpose: 'own_stay', sqft: o.sqft
      });
      out.tiers[t] = {
        worksLo: r100(b.total.low), worksHi: r100(b.total.high),
        lo: r100(f.fee + b.total.low), hi: r100(f.fee + b.total.high)
      };
    });
    return out;
  }

  /* The design fee alone, from sib-estimator.js only (10KB, loaded up front), so the fee can
     answer a tap before the renovation engine has arrived. */
  function feeOnly(o) {
    var f = fee(o);
    return { fee: f.fee, charged: f.chargeN, free: f.freeN, std: f.stdN, opn: f.opnN,
      rateStd: root.SIBEstimator.RATE.d.std, rateOpn: root.SIBEstimator.RATE.d.opn };
  }
  function feeReady() { return !!root.SIBEstimator; }

  root.SIBPriceHome = { load: load, ready: ready, feeReady: feeReady, price: price, feeOnly: feeOnly, roomCounts: roomCounts, works: works };
})(typeof self !== 'undefined' ? self : this);
